// src/ui/AvatarViewer.jsx — the profile picture, large, with nothing on it.
//
// 🟥 "WITHOUT ANY CONTAINER WITH X BUTTON." The client already has MediaViewer,
//    and it is right for what it shows: a header bar, the sender's name, a
//    download control, a filmstrip. None of those has anything to say about a
//    profile picture — there is nothing to download, nobody to attribute it to,
//    and one thing to look at.
//
// 🟢 SO THE ONLY THINGS OVER THE PICTURE ARE THE ONES THAT SAY WHAT IT IS: a
//    name, and a count when there is more than one page. Closing is the whole
//    surface — click anywhere, or press Escape — which is larger than any X.
//
// 🟢 AND THE PAGES COME FROM services/avatarPages.js, generated from the phone's
//    own rule, so "1 / 4" counts the same four people in both clients.
import React, { useEffect, useState, useRef } from 'react';
import { pageLabel } from '../services/avatarPages';
import { DocText, Sparkles } from './icons';
/* 🧑‍🎨 a page with an avatar is the figure itself, full body, rollable */
import Avatar3D from './Avatar3D';

const initialOf = (name) => {
  const c = String(name || '').trim()[0];
  return c ? c.toUpperCase() : '';
};

function Page({ page }) {
  /* 🟢 THE TWO THAT HAVE NO PHOTOGRAPH, drawn rather than fetched — that is what
     they look like, not a photo that failed to load. The same two glyphs the
     phone draws, at the size the photo would have been. */
  if (page.glyph === 'ai') return <span className="av-glyph"><Sparkles width="100%" height="100%" /></span>;
  if (page.avatar) {
    const w = Math.min(420, Math.round(window.innerWidth * 0.8)), h = Math.min(620, Math.round(window.innerHeight * 0.72));
    return <span onClick={(e) => e.stopPropagation()}><Avatar3D avatar={page.avatar} mode="fullbody" width={w} height={h} interactive returnAfterMs={0} /></span>;
  }
  if (page.uri) return <img className="av-photo" src={page.uri} alt="" />;
  if (page.glyph === 'note') return <span className="av-glyph"><DocText width="100%" height="100%" /></span>;
  /* 🟢 A PERSON WITH NO PHOTO IS STILL SOMEBODY — their letter, at the size the
     photo would have been. Not an error, and not an empty screen. */
  return <span className="av-letter">{initialOf(page.name)}</span>;
}

export default function AvatarViewer({ pages, onClose, initialIndex = 0 }) {
  const [i, setI] = useState(initialIndex);
  const touch = useRef(null);
  const many = (pages?.length || 0) > 1;

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      if (!many) return;
      if (e.key === 'ArrowRight') setI((x) => (x + 1) % pages.length);
      if (e.key === 'ArrowLeft') setI((x) => (x - 1 + pages.length) % pages.length);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, many, pages]);

  /* 🟥 NOTHING TO SHOW IS NOT A BLACK SCREEN. A group with no photo and no
     members is the one case where the click has no answer, and opening on
     nothing reads as a bug rather than as an empty group. */
  if (!pages?.length) return null;
  const at = Math.min(i, pages.length - 1);
  const page = pages[at];
  const label = pageLabel(at, pages.length);

  return (
    <div
      className="avatar-viewer"
      onClick={onClose}
      onTouchStart={(e) => { touch.current = e.touches[0]?.clientX ?? null; }}
      onTouchEnd={(e) => {
        if (touch.current == null || !many) return;
        const dx = (e.changedTouches[0]?.clientX ?? 0) - touch.current;
        if (Math.abs(dx) > 60) setI((x) => (x + (dx < 0 ? 1 : -1) + pages.length) % pages.length);
        touch.current = null;
      }}
    >
      <div className="av-stage"><Page page={page} /></div>
      {/* 🟢 THE ARROWS ARE THE ONLY CONTROLS, and only when there is somewhere to
          go. They stop the click, or paging would close the viewer. */}
      {many && (
        <>
          <button className="av-arrow left" aria-label="Previous"
            onClick={(e) => { e.stopPropagation(); setI((x) => (x - 1 + pages.length) % pages.length); }}>‹</button>
          <button className="av-arrow right" aria-label="Next"
            onClick={(e) => { e.stopPropagation(); setI((x) => (x + 1) % pages.length); }}>›</button>
        </>
      )}
      <div className="av-caption">
        {page.name ? <b>{page.name}</b> : null}
        {label ? <i>{label}</i> : null}
      </div>
    </div>
  );
}
